/**
 * ReferredByDialog: the client's own Referred by (0221), from the People
 * tab's line. The same field the intake form uses (ReferralSourceField):
 * link a Directory organization or person, type the referrer as text, or
 * clear it. Saving sends exactly one non-null arm, or all three null, to
 * PUT /clients/{id}/referred-by; the record refetches, so a cleared
 * client falls back to the intake card's referral if there is one.
 *
 * Opened only for manage_client_identity holders (the server's gate).
 */

import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { ReferralSourceField } from '@/components/workspace/ReferralSourceField';
import { useSetClientReferredBy } from '@/lib/clientsApi';
import { useSupportContact } from '@/hooks/useSupportContact';
import { pageWriteText } from '@/lib/errorCopy';
import type { ReferredBy } from '@/lib/clientRecord';

interface Referral {
  referral_org_id: string | null;
  referral_individual_id: string | null;
  referral_text: string | null;
}

/** The client's own referral as the dialog opens: the intake's fallback
 *  is not the client's, so it starts empty. */
function initialReferral(current: ReferredBy | null): Referral {
  if (current === null || current.source !== 'client') {
    return { referral_org_id: null, referral_individual_id: null, referral_text: null };
  }
  if (current.orgId !== null) return { referral_org_id: current.orgId, referral_individual_id: null, referral_text: null };
  if (current.individualId !== null) {
    return { referral_org_id: null, referral_individual_id: current.individualId, referral_text: null };
  }
  return { referral_org_id: null, referral_individual_id: null, referral_text: current.label };
}

export function ReferredByDialog({ current, onClose }: { current: ReferredBy | null; onClose: () => void }): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const save = useSetClientReferredBy(tenantSlug);
  const contact = useSupportContact();
  const [referral, setReferral] = useState<Referral>(() => initialReferral(current));
  const [linkedName, setLinkedName] = useState<string | null>(
    current?.source === 'client' && (current.orgId !== null || current.individualId !== null) ? current.label : null,
  );

  async function pressSave(): Promise<void> {
    if (save.isPending) return;
    try {
      await save.mutateAsync({ id: clientId, referral });
      onClose();
    } catch {
      // The sentence renders from save.error below.
    }
  }

  return (
    <Dialog
      title="Referred by"
      size="md"
      testId="referred-by-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={save.isPending}
            loading={save.isPending}
            data-testid="referred-by-save"
            onClick={() => void pressSave()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="py-2">
        <ReferralSourceField
          referralText={referral.referral_text}
          referralOrgId={referral.referral_org_id}
          referralIndividualId={referral.referral_individual_id}
          linkedName={linkedName}
          onChange={(edits) => {
            // One arm at a time: a link clears the text, text clears a
            // link, and an unlink leaves all three empty.
            const next: Referral = { referral_org_id: null, referral_individual_id: null, referral_text: null };
            if (edits.referral_org_id) next.referral_org_id = edits.referral_org_id;
            else if (edits.referral_individual_id) next.referral_individual_id = edits.referral_individual_id;
            else if (edits.referral_text) next.referral_text = edits.referral_text;
            if (!next.referral_org_id && !next.referral_individual_id) setLinkedName(null);
            setReferral(next);
          }}
          onLink={(row) => setLinkedName(row.display_name)}
        />
        {save.error && (
          <p role="alert" data-testid="referred-by-error" className="mt-2 text-xs text-pepper">
            {pageWriteText(save.error, contact)}
          </p>
        )}
      </div>
    </Dialog>
  );
}
